import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { format } from "date-fns";

import TaskDetailFilters from "./TaskDetailFilters";
import TaskDetailList from "./TaskDetailList";

import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";
import * as exportActions from "../../../actions/exportActions";
import * as serviceActions from "../../../actions/serviceActions";

import { customToast as toast } from "../../../utils";

import { downloadCSV, escapeCSVString } from "../utils";

import { formatDate } from "../../../helpers/date";

import { TASK_DETAIL_COLUMNS } from "./columns";

const TaskDetailPage = ({
  codes,
  actions,
  exporting,
  exported,
  service,
  auth
}) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 50,
    filtered: {
      startDate: format(new Date(new Date().getFullYear(), 0, 1), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd")
    },
    sorted: [{ id: "service_task.task.description", desc: true }]
  });
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [count, setCount] = useState(0);
  const [exportingCSV, setExportingCSV] = useState(false);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  useEffect(() => {
    if (filters.filtered.service) {
      actions.getService(filters.filtered.service.value);
    }
  }, [actions, filters.filtered.service]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Task-Detail-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = async (
    pageSize = filters.pageSize,
    page = 0,
    filtered = filters.filtered,
    sorted = filters.sorted
  ) => {
    if (filters.filtered.startDate && filters.filtered.endDate) {
      setLoading(true);
      try {
        const report = await actions.getTaskDetailReport({
          filtered,
          page,
          pageSize,
          sorted
        });
        if (report) {
          setData(report.tasks);
          setCount(report.count);
          setSummary(report.summary);
        }
      } catch (error) {
        toast.error(
          "Request too large. Please adjust filters or contact support."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      (name === "project" ||
        name === "department" ||
        name === "customer" ||
        name === "stage")
    ) {
      setFilters((prev) => ({
        ...prev,
        filtered: {
          ...prev.filtered,
          [name]: value.map((v) => ({ label: v.label, value: v.value }))
        }
      }));
    } else {
      const updates = { [name]: value };
      if (name === "service") {
        updates.service_task = null;
      }
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, ...updates }
      }));
    }
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize }));
    fetchData(pageSize, filters.page);
  };

  const handlePageChange = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchData(filters.pageSize, page);
  };

  const handleSortedChange = async (newSort) => {
    setFilters((prev) => ({ ...prev, sorted: newSort }));
    fetchData(filters.pageSize, filters.page, filters.filtered, newSort);
  };

  const handleResetClick = () => {
    setFilters({
      page: 0,
      pageSize: 50,
      filtered: {
        startDate: format(
          new Date(new Date().getFullYear(), 0, 1),
          "yyyy-MM-dd"
        ),
        endDate: format(new Date(), "yyyy-MM-dd")
      }
    });
    setData([]);
    setSummary([]);
  };

  const handleExport = async () => {
    setExportingCSV(true);
    const report = await actions.getTaskDetailReport({
      filtered: filters.filtered,
      sorted: filters.sorted
    });
    if (report) {
      downloadCSV(
        `TaskDetail-${formatDate(new Date(), "yyyyMMdd")}`,
        report.tasks.map((item) => ({
          ...item,
          actual_duration:
            // eslint-disable-next-line no-nested-ternary
            item.duration_units === "Hours"
              ? item.actual_duration
              : item.duration_units === "Minutes" && item.actual_duration
                ? (item.actual_duration / 60).toFixed(2)
                : 0,
          billing_description: item.billing_description
            ? escapeCSVString(item.billing_description)
            : "",
          review_notes: item.review_notes
            ? escapeCSVString(item.review_notes)
            : ""
        })),
        TASK_DETAIL_COLUMNS
      );
    }
    setExportingCSV(false);
  };

  const handleExportPDF = () => {
    const queryFilters = {
      filtered: filters.filtered
    };
    const payload = {
      data: queryFilters,
      type: "TASK_DETAIL",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div id="task-detail-page" className="report-page">
      <TaskDetailFilters
        codes={codes}
        filters={filters}
        onChange={handleChange}
        onFetchClick={fetchData}
        onResetClick={handleResetClick}
        loading={loading}
        disabled={
          loading ||
          !Object.keys(filters).length ||
          !filters.filtered.startDate ||
          !filters.filtered.endDate
        }
        onCSVExport={handleExport}
        onPDFExport={handleExportPDF}
        exportDisabled={!data?.length || loading || exporting || exportingCSV}
        service={service}
        auth={auth}
      />
      <TaskDetailList
        data={data}
        summary={summary.filter((s) => s.serviceDescription)}
        filtered={filters}
        loading={loading}
        count={count}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        sorted={filters.sorted}
        pageSize={filters.pageSize}
        defaultPageSize={50}
      />
    </div>
  );
};

TaskDetailPage.propTypes = {
  codes: PropTypes.array,
  actions: PropTypes.object,
  exporting: PropTypes.bool,
  exported: PropTypes.string,
  service: PropTypes.object,
  auth: PropTypes.object
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes,
  exporting: state.reducers.exporting,
  exported: state.reducers.exported,
  service: state.reducers.service,
  auth: state.reducers.auth
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions,
      ...exportActions,
      ...serviceActions
    },
    dispatch
  )
});

export default connect(mapStatesToProps, mapDispatchToProps)(TaskDetailPage);
